import {
  type TransitionEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const PINNED_SLIDE_MS = 200;

export type UsePinnedSlideOpenOptions = {
  onPinnedCloseComplete?: () => void;
  open: boolean;
  pinned: boolean;
};

export function usePinnedSlideOpen({
  onPinnedCloseComplete,
  open,
  pinned,
}: UsePinnedSlideOpenOptions) {
  const [slideOpen, setSlideOpen] = useState(open);
  const [isClosing, setIsClosing] = useState(false);
  const wasOpenRef = useRef(open);
  const closeFinishedRef = useRef(false);
  const onPinnedCloseCompleteRef = useRef(onPinnedCloseComplete);
  const pinnedRef = useRef(pinned);
  onPinnedCloseCompleteRef.current = onPinnedCloseComplete;
  pinnedRef.current = pinned;

  const finishClose = useCallback(() => {
    if (closeFinishedRef.current) {
      return;
    }

    closeFinishedRef.current = true;
    setIsClosing(false);

    if (pinnedRef.current) {
      onPinnedCloseCompleteRef.current?.();
    }
  }, []);

  useEffect(() => {
    if (open) {
      closeFinishedRef.current = false;
      setIsClosing(false);
      wasOpenRef.current = true;

      if (!pinned) {
        setSlideOpen(true);
        return;
      }

      const frame = requestAnimationFrame(() => {
        setSlideOpen(true);
      });

      return () => cancelAnimationFrame(frame);
    }

    setSlideOpen(false);

    if (wasOpenRef.current) {
      setIsClosing(true);
    }

    wasOpenRef.current = false;
    return;
  }, [open, pinned]);

  useEffect(() => {
    if (!isClosing) {
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      finishClose();
    }, PINNED_SLIDE_MS);

    return () => globalThis.clearTimeout(timeout);
  }, [finishClose, isClosing]);

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      if (event.propertyName !== 'transform') {
        return;
      }

      if (!open && isClosing) {
        finishClose();
      }
    },
    [finishClose, isClosing, open]
  );

  return {
    handleTransitionEnd,
    isAccessibilityVisible: open || isClosing,
    isVisuallyOpen: pinned ? slideOpen : open,
  };
}
